import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { createStore } from '@ngneat/elf';
import { deleteEntities, selectAllEntities, selectEntity, setEntities, updateEntities, upsertEntities, withEntities } from '@ngneat/elf-entities';
import { tap, first, switchMap, shareReplay } from 'rxjs';

import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { Models } from '../../models/global.models';
import { WithId } from './store.models';

const store = createStore({ name: 'tags' }, withEntities<WithId<Models.Tag, 'guid'>, 'guid'>({ idKey: 'guid' }));

/** All available tags */
@Injectable({ providedIn: 'root' })
export class TagsRepository {
  private readonly _http = inject(HttpClient);
  private readonly _environment = inject(APP_ENV);

  private _baseUrl = this._environment.retail.apiUrl;

  all$ = this.get().pipe(shareReplay(1));

  reset() {
    store.reset();
  }

  get() {
    return this._http.get<WithId<Models.Tag, 'guid'>[]>(`${this._baseUrl}/tags`).pipe(
      tap((tags) => {
        store.update(setEntities(tags));
      }),
      switchMap(() => store.pipe(selectAllEntities())),
    );
  }

  post(tag: Models.Tag) {
    return this._http.post<WithId<Models.Tag, 'guid'>>(`${this._baseUrl}/tags`, tag).pipe(
      tap((response) => {
        store.update(upsertEntities(response));
      }),
    );
  }

  patch(tag: Models.Tag) {
    return this._http.patch(`${this._baseUrl}/tags/${tag.guid}`, tag).pipe(
      tap(() => {
        store.update(updateEntities(tag.guid, (entity) => ({ ...entity, ...tag })));
      }),
      switchMap(() => store.pipe(selectEntity(tag.guid))),
      first(),
    );
  }

  delete(guid: string) {
    return this._http.delete(`${this._baseUrl}/tags/${guid}`).pipe(
      tap(() => {
        store.update(deleteEntities([guid]));
      }),
    );
  }
}
